/**
 * Web session after email/password login.
 * Server sets HttpOnly cookie `sc_session`; SPA also keeps profile in localStorage
 * and sends Bearer when available (dual path). All engine fetches use credentials:include.
 *
 * Note: do not import ./config here — config imports getBearerToken (cycle).
 */

const STORAGE_KEY = "sc_web_session_v1";

function engineBase(): string {
  const raw = import.meta.env?.VITE_SEMANTICAD_ENGINE_URL as string | undefined;
  if (raw !== undefined && raw !== null) {
    return String(raw).trim().replace(/\/+$/, "");
  }
  // Match config.ts: same-origin in Vite dev (proxy), absolute URL otherwise.
  if (typeof import.meta !== "undefined" && import.meta.env?.DEV) return "";
  return "http://127.0.0.1:9472";
}

export type WebSession = {
  token: string;
  email: string;
  displayName?: string | null;
  organizationSlug: string;
  organizationName?: string | null;
  expiresAt?: string | null;
};

export function loadWebSession(): WebSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as WebSession;
    if (!s?.token && !s?.email) return null;
    if (s.expiresAt && Date.parse(s.expiresAt) < Date.now()) {
      clearWebSession();
      return null;
    }
    // token may be empty if we only rely on cookie; keep session shell for UI
    return s;
  } catch {
    return null;
  }
}

function notifySessionChanged(): void {
  try {
    window.dispatchEvent(new Event("sc-session-changed"));
  } catch {
    /* ignore SSR / non-window */
  }
}

export function saveWebSession(session: WebSession): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  notifySessionChanged();
}

export function clearWebSession(): void {
  localStorage.removeItem(STORAGE_KEY);
  notifySessionChanged();
}

export function getBearerToken(): string | null {
  return loadWebSession()?.token ?? null;
}

/**
 * Revoke server session (cookie + optional Bearer) then clear local storage.
 *
 * Idempotent: 401/403 means the token is already dead (DB wipe, expiry) — still
 * clear local state so the SPA is not stuck with a zombie Bearer.
 * Network failures still throw (cannot know if server session lives).
 */
export async function logoutWebSession(): Promise<void> {
  const token = getBearerToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  let res: Response;
  try {
    res = await fetch(`${engineBase()}/v1/auth/logout`, {
      method: "POST",
      credentials: "include",
      headers,
    });
  } catch (err) {
    throw new Error(
      `Logout failed (network) — session may still be active: ${
        err instanceof Error ? err.message : String(err)
      }`,
    );
  }
  // Already logged out / invalid token: clear local and succeed
  if (res.status === 401 || res.status === 403 || res.status === 404) {
    clearWebSession();
    return;
  }
  if (!res.ok) {
    throw new Error(`Logout failed (${res.status}) — session may still be active`);
  }
  clearWebSession();
}

/**
 * Drop zombie local session after engine 401 (e.g. Postgres reseed wiped auth_sessions).
 */
export function handleUnauthorizedSession(): void {
  if (!loadWebSession()) return;
  clearWebSession();
}
